import type { VoucherSpreadProps } from "@/types/home-type";
import { useState } from "react";
import { useTranslation } from "react-i18next";

export default function VoucherSpread({
    vouchers,
    locale,
}: VoucherSpreadProps) {
    const { t } = useTranslation();

    const [isVoucherSpread, setIsVoucherSpread] = useState(false);
    const voucherSpread = vouchers.map((v, i) => {
        const rotations = [
            isVoucherSpread
                ? "top-0 rotate-0 hover:-translate-y-2 z-[1]"
                : "bottom-12 -rotate-12 group-hover:-rotate-15 z-[1]",
            isVoucherSpread
                ? "top-60 rotate-0 hover:-translate-y-2 z-[2]"
                : "bottom-6 -rotate-6 group-hover:-rotate-8 z-[2]",
            isVoucherSpread
                ? "top-120 rotate-0 hover:-translate-y-2 z-[3]"
                : "bottom-0 rotate-3 group-hover:rotate-6 z-[3]",
        ];

        return {
            ...v,
            className: rotations[i],
        };
    });
    return (
        <div
            onClick={() => setIsVoucherSpread(!isVoucherSpread)}
            className={`relative ${isVoucherSpread ? "h-187.5 md:h-195" : "h-75 md:h-85"} max-w-sm mx-auto w-full group cursor-pointer perspective-1000 transition-all duration-500`}>
            {voucherSpread.map((v, i) => {
                const isShipping = v.type === "Shipping";
                return (
                    <div
                        key={`home-voucher-spread-${v.id}`}
                        className={`absolute inset-x-0 ${i === 1 ? "bg-white dark:bg-dark-2 border-2 border-gold/50" : "bg-linear-to-br from-white to-cream dark:from-dark-2 dark:to-dark border-2 border-gold"} h-52 rounded-3xl transform transition-all duration-500 shadow-[0_20px_50px_rgba(184,137,42,0.3)] dark:shadow-[0_20px_50px_rgba(0,0,0,0.8)] p-6 flex flex-col justify-between ${v.className}`}>
                        <div className="flex justify-end items-start">
                            <span className="text-[10px] bg-gold/10 dark:bg-gold-light/20 border border-gold/50 dark:border-gold-light/50 text-gold dark:text-gold-light uppercase px-3 py-1 -my-1 rounded tracking-wider">
                                {isShipping
                                    ? t("home.voucher.shipping")
                                    : t("home.voucher.product")}
                            </span>
                        </div>

                        <div>
                            <div className="text-6xl font-serif font-extrabold text-dark dark:text-white leading-none mb-2">
                                {v.format === "Persentage" ? (
                                    <>
                                        {v.value[locale]}
                                        <span className="text-gold dark:text-gold-light text-3xl">
                                            %
                                        </span>
                                    </>
                                ) : (
                                    <>
                                        {isShipping
                                            ? "FREE"
                                            : locale === "id"
                                              ? `${Math.floor(v.value[locale] / 1000)}K`
                                              : `${locale === "en" ? "$" : "RM"} ${v.value[locale]}`}
                                    </>
                                )}
                            </div>

                            <div className="text-[#7A6848] dark:text-white/50 text-xs line-clamp-1 tracking-widest uppercase">
                                {v.title[locale]}
                            </div>
                        </div>

                        <div className="mt-4 pt-4 border-t border-dashed border-gold/50 flex justify-between items-center">
                            <div className="font-mono text-gold dark:text-gold-light bg-gold/10 dark:bg-gold-light/10 rounded border border-gold/40 dark:border-gold-light/40 px-3 py-1 tracking-[0.2em]">
                                {v.code}
                            </div>

                            <div className="text-[#7A6848]/60 dark:text-white/30 text-[10px] text-right ml-2">
                                {v.minPurchase[locale] > 0 ? (
                                    <>
                                        Min.{" "}
                                        {new Intl.NumberFormat(
                                            locale === "en"
                                                ? "en-US"
                                                : locale === "my"
                                                  ? "ms-MY"
                                                  : "id-ID",
                                            {
                                                style: "currency",
                                                currency:
                                                    locale === "en"
                                                        ? "USD"
                                                        : locale === "my"
                                                          ? "MYR"
                                                          : "IDR",
                                                maximumFractionDigits:
                                                    locale === "id" ? 0 : 2,
                                            },
                                        ).format(v.minPurchase[locale])}
                                    </>
                                ) : (
                                    <span>
                                        {t("home.voucher.min_purchase")}
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                );
            })}

            {!isVoucherSpread && (
                <div className="absolute top-1 md:-top-4 right-0 bg-gold-light hover:bg-gold dark:bg-gold dark:hover:bg-gold-light text-dark px-3 py-1 rounded-full text-xs font-bold shadow-lg animate-bounce z-20">
                    Click me!
                </div>
            )}
        </div>
    );
}
